import React, { useCallback, useEffect, useState } from 'react'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import AppRankingRewardTabs from './AppRankingRewardTabs'

const OFFSET = 20

const RANKING_TYPE_LABELS = {
    1: '러닝 주간',
    2: '러닝 월간',
    3: '만보기 주간',
    4: '만보기 월간',
}

// 서버 status 값과 화면 문구를 한 곳에서 맞춘다.
const STATUS_LABELS = {
    pending: '발송 전',
    sent: '지급 완료',
    dryrun: '테스트(발송 안 함)',
    failed: '발송 실패',
    balance_insufficient: '비즈머니 부족',
    manual_sent: '수동 완료',
    skipped_test: '미지급 · 테스트 계정',
    skipped_blocked: '미지급 · 정지·탈퇴',
    skipped_overseas: '미지급 · 해외 유저',
    skipped_no_phone: '미지급 · 휴대폰 없음',
    skipped_region: '미지급 · 노출 국가 아님',
}

const STATUS_FILTER_OPTIONS = [
    { value: '', label: '전체 상태' },
    { value: 'pending,failed,balance_insufficient', label: '처리 필요 (발송 전·실패·잔액 부족)' },
    { value: 'sent,manual_sent', label: '지급 완료' },
    { value: 'dryrun', label: '테스트(dryrun)' },
    { value: 'skipped_test,skipped_blocked,skipped_overseas,skipped_no_phone,skipped_region', label: '미지급' },
]

const RETRYABLE_STATUSES = ['pending', 'failed', 'balance_insufficient', 'dryrun']

const SNAPSHOT_SOURCE_LABELS = {
    redis: '화면 순위',
    none: '스냅샷 없음',
}

const TABLE_COLUMNS = [
    { width: 90 },
    { width: 210 },
    { width: 120 },
    { width: 80 },
    { width: 180 },
    { width: 220 },
    { width: 180 },
    { width: 240 },
    { width: 200 },
]

const TABLE_MIN_WIDTH = TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)
const NOWRAP_CELL_SX = { whiteSpace: 'nowrap' }
const TEXT_CELL_SX = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

const formatReward = (row) => {
    if (row.rewardType === 'point') {
        return `포인트 ${Number(row.pointAmount || 0).toLocaleString()}P`
    }

    return `기프티콘 · ${row.goodsName || row.goodsCode || '-'}`
}

const formatPeriodOption = (period) => {
    const typeLabel = RANKING_TYPE_LABELS[period.rankingType] || period.rankingType
    const sourceLabel = SNAPSHOT_SOURCE_LABELS[period.source] || period.source
    const rewardedLabel = period.rewardedAt ? '지급 완료' : '지급 전'

    return `${typeLabel} ${period.periodStart} ~ ${period.periodEnd} · ${sourceLabel} · ${rewardedLabel}`
}

export default function AppRankingRewards() {
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [rows, setRows] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [curPage, setCurPage] = useState(1)
    const [periods, setPeriods] = useState([])
    const [periodKey, setPeriodKey] = useState('')
    const [rankingType, setRankingType] = useState('')
    const [status, setStatus] = useState('')
    const [listErrorMessage, setListErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    const notify = (severity, message) => {
        setOpenSnackbar(true)
        setSeveritySnackbar(severity)
        setMessageSnackbar(message)
    }

    const getPeriods = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/rankingReward/getPeriodList', { limit: 60 })

            if (data.code === 200) {
                setPeriods(data.data.rows || [])
            }
        } catch (_error) {
            // 필터 목록은 없어도 지급 내역 조회는 계속할 수 있다.
        }
    }, [])

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/rankingReward/getGrantList', {
                page: curPage,
                offset: OFFSET,
                periodKey: periodKey || undefined,
                rankingType: rankingType || undefined,
                status: status || undefined,
            })

            if (data.code === 200) {
                setRows(data.data.rows || [])
                setTotalCount(data.data.count || 0)
            } else {
                const errorMessage = data.message || '지급 내역을 불러오지 못했습니다.'
                setRows([])
                setTotalCount(0)
                setListErrorMessage(errorMessage)
                notify('error', errorMessage)
            }
        } catch (_error) {
            const errorMessage = '지급 내역을 불러오지 못했습니다.'
            setRows([])
            setTotalCount(0)
            setListErrorMessage(errorMessage)
            notify('error', errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, periodKey, rankingType, status])

    useEffect(() => {
        getPeriods()
    }, [getPeriods])

    useEffect(() => {
        getData()
    }, [getData])

    const handleFilterChange = (setter) => (event) => {
        setCurPage(1)
        setter(event.target.value)
    }

    const runGrantAction = async (url, grantId, successMessage) => {
        if (actionLoading) {
            return
        }

        setActionLoading(true)

        try {
            const { data } = await API.post(url, { grantId })

            if (data.code === 200) {
                notify('success', `${successMessage} (${STATUS_LABELS[data.data?.status] || data.data?.status || ''})`)
                await getData(false)
            } else {
                notify('error', data.message || '처리하지 못했습니다.')
            }
        } catch (_error) {
            notify('error', '처리하지 못했습니다.')
        } finally {
            setActionLoading(false)
        }
    }

    const handleResend = (row) => {
        if (!window.confirm(`${row.ranking}위 ${row.nickName || row.userId} 님에게 기프티콘을 다시 발송합니다. 계속할까요?`)) {
            return
        }

        runGrantAction('/api/v1/admin/rankingReward/resendGifticon', row.grantId, '재발송했습니다.')
    }

    const handleCompleteManually = (row) => {
        if (!window.confirm('기프티쇼에서 직접 발송한 건만 수동 완료로 처리하세요. 계속할까요?')) {
            return
        }

        runGrantAction('/api/v1/admin/rankingReward/completeManually', row.grantId, '수동 완료로 변경했습니다.')
    }

    return (
        <Grid item xs={12}>
            {loading ? <Spiner /> : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>App 랭킹경품 지급 내역</Title>
                    <AppRankingRewardTabs current="grants" disabled={actionLoading} />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <TextField
                            select
                            size="small"
                            label="기간"
                            value={periodKey}
                            disabled={actionLoading}
                            onChange={handleFilterChange(setPeriodKey)}
                            sx={{ minWidth: 380 }}
                        >
                            <MenuItem value="">전체 기간</MenuItem>
                            {periods.map((period) => (
                                <MenuItem key={period.periodKey} value={period.periodKey}>
                                    {formatPeriodOption(period)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="구분"
                            value={rankingType}
                            disabled={actionLoading}
                            onChange={handleFilterChange(setRankingType)}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">전체 구분</MenuItem>
                            {Object.entries(RANKING_TYPE_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="상태"
                            value={status}
                            disabled={actionLoading}
                            onChange={handleFilterChange(setStatus)}
                            sx={{ minWidth: 300 }}
                        >
                            {STATUS_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <Button variant="outlined" size="small" disabled={actionLoading} onClick={() => getData(false)}>
                            새로 조회
                        </Button>
                    </Box>

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {TABLE_COLUMNS.map((column, index) => (
                                    <col key={`grant-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>지급ID</TableCell>
                                    <TableCell>기간</TableCell>
                                    <TableCell>구분</TableCell>
                                    <TableCell>순위</TableCell>
                                    <TableCell>유저</TableCell>
                                    <TableCell>경품</TableCell>
                                    <TableCell>상태</TableCell>
                                    <TableCell>비고</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length > 0 ? rows.map((row) => (
                                    <TableRow key={row.grantId}>
                                        <TableCell>{row.grantId}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.periodKey}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{RANKING_TYPE_LABELS[row.rankingType] || row.rankingType}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.ranking}위</TableCell>
                                        <TableCell sx={TEXT_CELL_SX} title={`${row.nickName || ''} (${row.userId})`}>
                                            {row.nickName || '-'} ({row.userId})
                                            {row.phone ? ` · ${row.phone}` : ''}
                                        </TableCell>
                                        <TableCell sx={TEXT_CELL_SX} title={formatReward(row)}>{formatReward(row)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{STATUS_LABELS[row.status] || row.status}</TableCell>
                                        <TableCell sx={TEXT_CELL_SX} title={row.errorMessage || row.giftOrderNo || ''}>
                                            {row.errorMessage || row.giftOrderNo || (row.sentAt ? row.sentAt : '-')}
                                        </TableCell>
                                        <TableCell align="right" sx={NOWRAP_CELL_SX}>
                                            {row.rewardType === 'gifticon' && RETRYABLE_STATUSES.includes(row.status) ? (
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                    <Button size="small" variant="outlined" disabled={actionLoading} onClick={() => handleResend(row)}>
                                                        재발송
                                                    </Button>
                                                    <Button size="small" variant="outlined" color="warning" disabled={actionLoading} onClick={() => handleCompleteManually(row)}>
                                                        수동 완료
                                                    </Button>
                                                </Box>
                                            ) : '-'}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={TABLE_COLUMNS.length}>
                                            {listErrorMessage ? (
                                                <ListEmptyState
                                                    variant="error"
                                                    title="지급 내역을 불러오지 못했습니다."
                                                    description={listErrorMessage}
                                                    action={(
                                                        <Button variant="outlined" size="small" onClick={() => getData(false)} disabled={actionLoading}>
                                                            다시 조회
                                                        </Button>
                                                    )}
                                                />
                                            ) : (
                                                <ListEmptyState
                                                    title="조회된 지급 내역이 없습니다."
                                                    description="지급 크론이 돌면(주간 월요일 15:00, 월간 1일 15:00) 이 목록에 표시됩니다."
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={OFFSET} totalCount={totalCount} setCurPage={setCurPage} />}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        기프티쇼에서 직접 발송했다면 반드시 수동 완료로 바꿔주세요. 그대로 두면 재발송으로 두 번 나갈 수 있습니다.
                    </Typography>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={() => setOpenSnackbar(false)} />}
                </Paper>
            )}
        </Grid>
    )
}
